/**
 * @fileoverview Strategy API Client - specialized client for strategy operations
 * Extends BaseApiClient with strategy-specific functionality
 */

import { BaseApiClient } from '../core/BaseApiClient';
import { ApiError } from '../core/errors';
import type { 
  ApiClientConfig, 
  PaginatedResponse,
  RequestOptions 
} from '../core/types';

// Strategy-specific types
export interface StrategyParameter {
  name: string;
  type: 'number' | 'boolean' | 'string' | 'select';
  defaultValue: number | boolean | string;
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
  description?: string;
}

export interface Strategy {
  id: string;
  name: string;
  description?: string;
  category: string;
  parameters: StrategyParameter[];
  isBuiltIn: boolean;
  isPremium: boolean;
  authorId?: string;
  authorName?: string;
  rating?: number;
  downloads?: number;
  supportedTradingModes?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UserStrategy {
  id: string;
  name: string;
  description?: string;
  strategyData: Record<string, unknown>; // StrategyGraph or visual definition
  isShared: boolean;
  shareId?: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
}

export interface CreateStrategyRequest {
  name: string;
  description?: string;
  strategyData: Record<string, unknown>;
  isShared?: boolean;
}

export interface UpdateStrategyRequest {
  name?: string;
  description?: string;
  strategyData?: Record<string, unknown>;
  isShared?: boolean;
}

export interface ShareStrategyRequest {
  isPublic: boolean;
  allowComments?: boolean;
  allowRating?: boolean;
}

export interface ShareStrategyResponse {
  shareId: string;
  shareUrl: string;
  isPublic: boolean;
}

export interface RateStrategyRequest {
  rating: number; // 1-5
  comment?: string;
}

export interface StrategyRating {
  id: string;
  strategyId: string;
  userId: string;
  rating: number;
  comment?: string;
  createdAt: string;
}

export interface StrategyFeatures {
  maxStrategies: number;
  currentCount: number;
  canCreateMore: boolean;
  premiumStrategiesAccess: boolean;
  communityStrategiesAccess: boolean;
}

export interface MarketplaceStrategy extends Strategy {
  shareId: string;
  isPublic: boolean;
  ratings: StrategyRating[];
  averageRating: number;
  ratingCount: number;
  downloadCount: number;
}

// Server response interface for the combined strategies endpoint
export interface StrategiesResponse {
  builtIn: Strategy[];
  user: UserStrategy[];
  shared: Strategy[];
}

export class StrategyApiClient extends BaseApiClient {
  constructor(config: { baseURL: string } & ApiClientConfig) {
    super(config);
  }

  /**
   * Get built-in strategies available to user
   */
  async getBuiltInStrategies(options?: RequestOptions): Promise<Strategy[]> {
    try {
      const response = await this.get<StrategiesResponse>('/strategies', options);
      return response.builtIn || [];
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get built-in strategies', 500, 'STRATEGIES_LIST_ERROR');
    }
  }

  /**
   * Get user's custom strategies
   */
  async getUserStrategies(options?: RequestOptions): Promise<UserStrategy[]> {
    try {
      const response = await this.get<StrategiesResponse>('/strategies', options);
      return response.user || [];
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get user strategies', 500, 'USER_STRATEGIES_ERROR');
    }
  }

  /**
   * Create a new user strategy
   */
  async createStrategy(request: CreateStrategyRequest, options?: RequestOptions): Promise<UserStrategy> {
    try {
      const response = await this.post<UserStrategy>('/strategies', request, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to create strategy', 500, 'STRATEGY_CREATE_ERROR');
    }
  }

  /**
   * Get a specific strategy by ID
   */
  async getStrategy(strategyId: string, options?: RequestOptions): Promise<UserStrategy> {
    try {
      const response = await this.get<UserStrategy>(`/strategies/${strategyId}`, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get strategy', 500, 'STRATEGY_GET_ERROR');
    }
  }

  /**
   * Update a user strategy
   */
  async updateStrategy(
    strategyId: string, 
    updates: UpdateStrategyRequest, 
    options?: RequestOptions
  ): Promise<UserStrategy> {
    try {
      const response = await this.put<UserStrategy>(`/strategies/${strategyId}`, updates, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to update strategy', 500, 'STRATEGY_UPDATE_ERROR');
    }
  }

  /**
   * Delete a user strategy
   */
  async deleteStrategy(strategyId: string, options?: RequestOptions): Promise<void> {
    try {
      await this.delete<void>(`/strategies/${strategyId}`, options);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to delete strategy', 500, 'STRATEGY_DELETE_ERROR');
    }
  }

  /**
   * Share a strategy publicly
   */
  async shareStrategy(
    strategyId: string, 
    shareRequest: ShareStrategyRequest, 
    options?: RequestOptions
  ): Promise<ShareStrategyResponse> {
    try {
      const response = await this.post<ShareStrategyResponse>(
        `/strategies/${strategyId}/share`,
        shareRequest,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to share strategy', 500, 'STRATEGY_SHARE_ERROR');
    }
  }

  /**
   * Rate a shared strategy
   */
  async rateStrategy(
    strategyId: string, 
    ratingRequest: RateStrategyRequest, 
    options?: RequestOptions
  ): Promise<StrategyRating> {
    try {
      const response = await this.post<StrategyRating>(
        `/strategies/${strategyId}/rate`,
        ratingRequest,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to rate strategy', 500, 'STRATEGY_RATE_ERROR');
    }
  }

  /**
   * Get user's strategy features and limits
   */
  async getUserFeatures(options?: RequestOptions): Promise<StrategyFeatures> {
    try {
      const response = await this.get<StrategyFeatures>('/strategies/user/features', options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get user features', 500, 'USER_FEATURES_ERROR');
    }
  }

  /**
   * Get marketplace strategies (public shared strategies)
   */
  async getMarketplaceStrategies(options?: RequestOptions & {
    page?: number;
    limit?: number;
    category?: string;
    search?: string;
    sortBy?: 'rating' | 'downloads' | 'created' | 'name';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<MarketplaceStrategy>> {
    try {
      const params: Record<string, string> = {};
      if (options?.page) params.page = options.page.toString();
      if (options?.limit) params.limit = options.limit.toString();
      if (options?.category) params.category = options.category;
      if (options?.search) params.search = options.search;
      if (options?.sortBy) params.sortBy = options.sortBy;
      if (options?.sortOrder) params.sortOrder = options.sortOrder;

      const response = await this.get<PaginatedResponse<MarketplaceStrategy>>(
        '/strategies/marketplace',
        { ...options, params }
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get marketplace strategies', 500, 'MARKETPLACE_ERROR');
    }
  }

  /**
   * Get a shared strategy by share ID
   */
  async getSharedStrategy(shareId: string, options?: RequestOptions): Promise<MarketplaceStrategy> {
    try {
      const response = await this.get<MarketplaceStrategy>(`/strategies/shared/${shareId}`, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get shared strategy', 500, 'SHARED_STRATEGY_ERROR');
    }
  }

  /**
   * Download a shared strategy to user's collection
   */
  async downloadStrategy(shareId: string, options?: RequestOptions): Promise<UserStrategy> {
    try {
      const response = await this.post<UserStrategy>(`/strategies/shared/${shareId}/download`, {}, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to download strategy', 500, 'STRATEGY_DOWNLOAD_ERROR');
    }
  }

  /**
   * Get strategy backtest capabilities
   */
  async getBacktestCapabilities(strategyId: string, options?: RequestOptions): Promise<{
    canBacktest: boolean;
    requiresPremium: boolean;
    parameterRanges: Record<string, { min: number; max: number; step: number }>;
  }> {
    try {
      const response = await this.get<{
        canBacktest: boolean;
        requiresPremium: boolean;
        parameterRanges: Record<string, { min: number; max: number; step: number }>;
      }>(`/strategies/${strategyId}/backtest-capabilities`, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get backtest capabilities', 500, 'BACKTEST_CAPABILITIES_ERROR');
    }
  }

  // ========== Template Strategy Cloning ==========

  /**
   * Get all template strategies with user's clone status
   */
  async getTemplates(options?: RequestOptions): Promise<Array<{
    id: string;
    name: string;
    type?: string;
    isCloned: boolean;
    clonedAt?: string;
  }>> {
    try {
      const response = await this.get<{ 
        data: Array<{
          id: string;
          name: string;
          type?: string;
          isCloned: boolean;
          clonedAt?: string;
        }>;
      }>(
        '/strategies/templates',
        options
      );
      return response.data || [];
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get template strategies', 500, 'TEMPLATES_ERROR');
    }
  }

  /**
   * Clone a template strategy and accept terms of use
   * User takes full ownership and responsibility for the cloned strategy
   */
  async cloneStrategy(strategyId: string, options?: RequestOptions): Promise<{
    clonedAt: string;
    agreedToTerms: boolean;
  }> {
    try {
      const response = await this.post<{
        clonedAt: string;
        agreedToTerms: boolean;
      }>(`/strategies/clone/${strategyId}`, {}, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to clone strategy', 500, 'CLONE_STRATEGY_ERROR');
    }
  }

  /**
   * Get list of strategies the user has cloned
   */
  async getClonedStrategies(options?: RequestOptions): Promise<Array<{
    strategyId: string;
    clonedAt: string;
    agreedToTerms: boolean;
  }>> {
    try {
      const response = await this.get<{
        data: Array<{
          strategyId: string;
          clonedAt: string;
          agreedToTerms: boolean;
        }>;
      }>('/strategies/cloned', options);
      return response.data || [];
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get cloned strategies', 500, 'CLONED_STRATEGIES_ERROR');
    }
  }
}
